'use client'

import { Trans } from '@lingui/react/macro'
import { FormField as FormFieldType } from '@/core/graphql/types'
import { Box, Button, Alert, CircularProgress, FormControl, MenuItem, Select, Typography } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { useEthicsForm } from '../model/useEthicsForm'
import { EthicsFormField } from './EthicsFormField'

type EthicsSubmissionFormProps = {
  patientId: string
}

export function EthicsSubmissionForm({ patientId }: EthicsSubmissionFormProps) {
  const {
    ethicsTemplate,
    ethicsTemplates,
    selectedTemplateId,
    isLoadingTemplates,
    templatesError,
    formData,
    formResetKey,
    validationErrors,
    submitSuccess,
    isSubmitting,
    handleTemplateChange,
    handleFieldChange,
    handleSubmit,
  } = useEthicsForm(patientId)
  const getTemplateTitle = (templateId: string) => {
    const template = ethicsTemplates.find((item) => item._id === templateId)
    return template ? template.title : ''
  }

  if (isLoadingTemplates) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (templatesError) {
    return (
      <Alert severity="error" sx={{ mb: 5, borderRadius: 3 }}>
        <Trans>Unable to load the ethics form. Please try again later.</Trans>
      </Alert>
    )
  }

  if (!ethicsTemplate) {
    return (
      <Alert severity="warning" sx={{ mb: 5, borderRadius: 3 }}>
        <Trans>No active ethics form is configured yet.</Trans>
      </Alert>
    )
  }

  return (
    <Box sx={{ mb: 5 }}>
      {ethicsTemplates.length > 1 && (
        <Box sx={{ mb: 4.5 }}>
          <Typography
            id="ethics-template-select-label"
            variant="overline"
            sx={{
              display: 'block',
              fontWeight: 800,
              letterSpacing: '0.14em',
              color: '#4a4454',
              mb: 1.5,
              ml: 0.5,
            }}
          >
            <Trans>Choose an ethics form</Trans>
          </Typography>
          <FormControl fullWidth>
            <Select
              labelId="ethics-template-select-label"
              value={selectedTemplateId}
              onChange={(event) => handleTemplateChange(event.target.value)}
              renderValue={(value) => getTemplateTitle(value)}
              sx={{
                borderRadius: 3,
                bgcolor: '#f3f4f5',
                color: '#191c1d',
                fontWeight: 700,
                '& .MuiSelect-nativeInput': {
                  display: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(204, 195, 215, 0.3)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(123, 63, 228, 0.32)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '2px solid rgba(123, 63, 228, 0.55)' },
              }}
            >
              {ethicsTemplates.map((template) => (
                <MenuItem key={template._id} value={template._id}>
                  {template.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {ethicsTemplate.description && (
            <Typography variant="body2" sx={{ mt: 1.5, px: 0.5, color: '#4a4454', lineHeight: 1.6 }}>
              {ethicsTemplate.description}
            </Typography>
          )}
        </Box>
      )}

      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
          <Trans>Your ethical question has been submitted successfully.</Trans>
        </Alert>
      )}

      {validationErrors._form && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
          {validationErrors._form}
        </Alert>
      )}

      <Box key={`${selectedTemplateId}-${formResetKey}`} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {ethicsTemplate.fields.map((field: FormFieldType) => (
          <EthicsFormField
            key={field.id}
            field={field}
            value={formData[field.id] || (field.type === 'Multiple' ? [] : '')}
            error={validationErrors[field.id]}
            onChange={handleFieldChange}
          />
        ))}

        <Box sx={{ pt: 1.5, mb: 1 }}>
          <Button
            fullWidth
            variant="contained"
            color="info"
            size="large"
            endIcon={!isSubmitting ? <SendIcon /> : undefined}
            disabled={isSubmitting}
            onClick={handleSubmit}
            sx={{
              fontWeight: 800,
              py: 2,
              borderRadius: 3,
              fontSize: '1rem',
              textTransform: 'none',
              '&:active': { transform: 'scale(0.98)' },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : <Trans>Submit a question</Trans>}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 1.75, px: { xs: 2, sm: 4 }, textAlign: 'center', color: '#4a4454', lineHeight: 1.45 }}>
            <Trans>By submitting this form, you agree that your data may be used to continuously improve our ethical protocols.</Trans>
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
